import { ref } from "vue";
import { defineStore } from "pinia";
import { UserManager, WebStorageStateStore } from "oidc-client-ts";
import { useAppConfigurationStore } from "@/stores/appconfig";
import type { IAppConfiguration } from "@/interfaces/IAppConfiguration";

type DemoUser = {
  token: string;
};

type ApplicationProfile = {
  innax_application?: string[];
};

const useAuthStore = defineStore("auth", () => {
  const mgr = ref<UserManager | null>(null);

  const demoUserAccessToken = ref<string | undefined>(undefined);

  const isDemoUser = ref(false);

  function init(options: IAppConfiguration) {
    mgr.value = new UserManager({
      authority: options.identityUrl,
      client_id: import.meta.env.VITE_VUE_APP_CLIENT_ID,
      redirect_uri: `${options.hostUrl}callback`,
      response_type: "code",
      scope: "openid profile email " + import.meta.env.VITE_VUE_APP_SCOPE,
      post_logout_redirect_uri: options.hostUrl,
      userStore: new WebStorageStateStore({ store: localStorage }),
      loadUserInfo: true,
      automaticSilentRenew: true,
      silent_redirect_uri: `${options.hostUrl}silent-renew`,
      accessTokenExpiringNotificationTimeInSeconds: 60,
    });
  }

  //voordat een functie aangeroepen wordt kan gescheckt worden of de mgr valid is
  //in new Vue() worden zowel pinia en router geinitialiseerd en daardoor wordt router.beforeEach aangeroepen
  //deze store kon nog niet geinitialiseerd worden daarvoor maar moet wel gebruikt worden
  //in vue3 kan dat anders en wel
  function checkMgr() {
    if (!mgr.value) {
      const appconfigStore = useAppConfigurationStore();
      init(appconfigStore.data);
    }
  }

  async function login() {
    checkMgr();
    // console.log('login');
    return await mgr.value!.signinRedirect();
  }

  async function logout() {
    checkMgr();
    // console.log('logout');
    const user = await mgr.value!.getUser();
    return await mgr.value!.signoutRedirect({ id_token_hint: user?.id_token });
  }

  function logoutDemo() {
    if (isDemoUser.value) {
      isDemoUser.value = false;
      demoUserAccessToken.value = undefined;
      return true;
    } else {
      return false;
    }
  }

  async function getAccessToken() {
    checkMgr();
    if (isDemoUser.value) {
      //console.log("getAccessToken, access_token demo: ", token);
      return demoUserAccessToken.value;
    } else {
      const user = await mgr.value!.getUser();
      if (user) {
        //console.log("getAccessToken, access_token user: ", user);
        return user.access_token;
      } else {
        return undefined;
      }
    }
  }

  async function signinRedirectCallback() {
    checkMgr();
    return await mgr.value!.signinRedirectCallback();
  }

  async function signinSilent(prm?: Parameters<UserManager["signinSilent"]>[0]) {
    checkMgr();
    return await mgr.value!.signinSilent(prm);
  }

  async function signinSilentCallback() {
    checkMgr();
    return await mgr.value!.signinSilentCallback();
  }

  async function isLoggedIn() {
    checkMgr();
    //console.log("authStore, isLoggedIn, isdemo?: ", isDemoUser.value);
    if (isDemoUser.value) {
      return true;
    } else {
      const user = await mgr.value!.getUser();
      const validUser = !!user && !user.expired;
      //console.log("authStore, isLoggedIn, validUser?: ", validUser);
      return validUser;
    }
  }

  function setDemoUserAccessToken(item: DemoUser) {
    isDemoUser.value = true;
    demoUserAccessToken.value = item.token;
  }

  async function isAllowedForApplication() {
    checkMgr();
    if (isDemoUser.value) {
      return true;
    } else {
      const user = await mgr.value!.getUser();
      const profile = user?.profile as ApplicationProfile | undefined;
      return profile?.innax_application?.includes(import.meta.env.VITE_VUE_APP_CLIENT_ID) ?? false;
    }
  }

  return { init, login, logout, logoutDemo, getAccessToken, signinSilent, signinSilentCallback, signinRedirectCallback, isLoggedIn, setDemoUserAccessToken, isAllowedForApplication };
});

export { useAuthStore };
